<template>
  <div v-if="modelValue" class="account-context-chat" data-testid="account-context-chat">
    <div class="account-context-chat__backdrop" @click="requestClose"></div>
    <div
      class="account-context-chat__shell"
      role="dialog"
      aria-modal="true"
      aria-label="Suggest account context"
      data-testid="account-context-chat-dialog"
    >
      <IQControlledOverlayHeader title="Suggest account context" @close="requestClose" />

      <div ref="messagesEl" class="account-context-chat__messages">
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="account-context-chat__message"
          :class="message.type"
        >
          <IQUserPrompt v-if="message.type === 'user'" :message="message.content" variant="light" />
          <IQAIResponse
            v-else
            :response="message.content"
            format="markdown"
            :isThinking="false"
            :currentReasoning="''"
            surface="light"
          />
        </div>

        <p v-if="isRequesting" class="account-context-chat__status" role="status">
          IQ is reading the website(s)…
        </p>
      </div>

      <div v-if="hasSuggestion" class="account-context-chat__confirmation">
        <label class="account-context-chat__label" for="account-context-suggestion">
          Suggested account context
        </label>
        <textarea
          id="account-context-suggestion"
          v-model="editableSuggestion"
          class="account-context-chat__suggestion"
          :class="{ 'account-context-chat__suggestion--error': hasMaxError }"
          :maxlength="maxLength"
          :disabled="saving"
          data-testid="account-context-suggestion"
        />
        <div class="account-context-chat__counter-row">
          <span v-if="hasMaxError" class="account-context-chat__reduce">
            Reduce by {{ overByFormatted }}
          </span>
          <span
            class="account-context-chat__counter"
            :class="{ 'account-context-chat__counter--error': hasMaxError }"
          >
            {{ charCountFormatted }} / {{ maxLengthFormatted }}
          </span>
        </div>

        <p
          v-if="shownSaveError"
          class="account-context-chat__error"
          role="alert"
          data-testid="account-context-chat-save-error"
        >
          {{ shownSaveError }}
        </p>

        <div class="account-context-chat__actions">
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Save context and websites"
            :disabled="!canConfirm"
            :loaderLeft="saving"
            data-testid="account-context-chat-save"
            @click="handleConfirm"
          />
          <StandardButton
            type="magenta"
            variant="outline"
            size="small"
            label="Cancel"
            :disabled="saving"
            @click="requestClose"
          />
        </div>
      </div>

      <div v-else class="account-context-chat__composer">
        <textarea
          v-model="urlsInput"
          class="account-context-chat__input"
          placeholder="Paste one or more URLs…"
          aria-label="Company website URLs"
          :disabled="isRequesting"
          data-testid="account-context-chat-input"
          @keydown.enter.exact.prevent="handleSend"
        />
        <StandardButton
          type="magenta"
          variant="primary"
          size="small"
          label="Send"
          :disabled="isRequesting || !urlsInput.trim()"
          :loaderLeft="isRequesting"
          data-testid="account-context-chat-send"
          @click="handleSend"
        />
      </div>

      <p
        v-if="error"
        class="account-context-chat__error"
        role="alert"
        data-testid="account-context-chat-error"
      >
        {{ error }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import IQControlledOverlayHeader from "@components/IQControlledOverlayHeader/IQControlledOverlayHeader.vue";
import IQUserPrompt from "@components/IQUserPrompt/IQUserPrompt.vue";
import IQAIResponse from "@components/IQAIResponse/IQAIResponse.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { suggestContextFromUrls, getSuggestContextErrorMessage } from "@/api";
import { ENTITY_CONTEXT_MAX_LENGTH } from "@components/utils/useContextSuggestion";
import { parseWebsiteUrls } from "@components/utils/companyWebsites";

const PROMPT =
  "To suggest an account context, I need your company website(s). Please paste one or more URLs.";
const SUGGESTION_READY =
  "Here is the suggested account context from the website(s) you provided. Review it below, then save it together with those websites.";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  saveError: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "confirm"]);

const messagesEl = ref(null);
const messages = ref([]);
const urlsInput = ref("");
const isRequesting = ref(false);
const error = ref("");
const suggestion = ref(null);
const editableSuggestion = ref("");
const submittedUrls = ref([]);
const confirmationError = ref("");
const maxLength = ENTITY_CONTEXT_MAX_LENGTH;

const hasSuggestion = computed(() => suggestion.value !== null);
const charCount = computed(() => (editableSuggestion.value || "").length);
const hasMaxError = computed(() => charCount.value > maxLength);
const overByFormatted = computed(() => (charCount.value - maxLength).toLocaleString("en-US"));
const charCountFormatted = computed(() => charCount.value.toLocaleString("en-US"));
const maxLengthFormatted = computed(() => maxLength.toLocaleString("en-US"));
const shownSaveError = computed(() => confirmationError.value || props.saveError);
const canConfirm = computed(
  () => !props.saving && !hasMaxError.value && !!editableSuggestion.value.trim()
);

function reset() {
  messages.value = [{ type: "ai", content: PROMPT }];
  urlsInput.value = "";
  isRequesting.value = false;
  error.value = "";
  suggestion.value = null;
  editableSuggestion.value = "";
  submittedUrls.value = [];
  confirmationError.value = "";
  scrollToBottom();
}

function addMessage(type, content) {
  messages.value.push({ type, content });
  scrollToBottom();
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesEl.value) {
      messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
    }
  });
}

async function handleSend() {
  if (isRequesting.value) return;
  const { urls, invalid } = parseWebsiteUrls(urlsInput.value);
  // Report malformed URLs before the "nothing to read" case: a single invalid
  // token produces no valid URLs, and the invalid-URL message is more precise.
  if (invalid.length) {
    error.value = `Enter a valid http(s) URL: ${invalid.join(", ")}`;
    return;
  }
  if (!urls.length) {
    error.value = "Paste at least one website URL.";
    return;
  }

  const typed = urlsInput.value.trim();
  error.value = "";
  urlsInput.value = "";
  addMessage("user", typed);
  isRequesting.value = true;

  try {
    const { data } = await suggestContextFromUrls(urls, props.accountId);
    const text = typeof data?.suggestion === "string" ? data.suggestion.trim() : "";
    if (!text) {
      error.value = "IQ did not return an account context. Please try again.";
      return;
    }
    submittedUrls.value = urls;
    suggestion.value = text;
    editableSuggestion.value = text;
    addMessage("ai", SUGGESTION_READY);
  } catch (requestError) {
    error.value = getSuggestContextErrorMessage(requestError);
  } finally {
    isRequesting.value = false;
  }
}

function handleConfirm() {
  if (!canConfirm.value) return;
  confirmationError.value = "";
  emit("confirm", {
    context: editableSuggestion.value.trim(),
    urls: [...submittedUrls.value],
  });
}

function requestClose() {
  if (props.saving) return;
  emit("update:modelValue", false);
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      reset();
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.account-context-chat {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.account-context-chat__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.45);
}

.account-context-chat__shell {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(720px, 100%);
  max-height: min(720px, calc(100vh - 48px));
  overflow: hidden;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 20px 40px rgba(16, 24, 40, 0.18);
}

.account-context-chat__messages {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-height: 240px;
  overflow-y: auto;
  padding: 20px;
}

.account-context-chat__message {
  display: flex;
  flex-direction: column;
}

.account-context-chat__message.user {
  align-items: flex-end;
}

.account-context-chat__status {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #667085;
}

.account-context-chat__confirmation,
.account-context-chat__composer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px 20px;
  border-top: 1px solid #eaecf0;
}

.account-context-chat__label {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  color: #344054;
}

.account-context-chat__suggestion,
.account-context-chat__input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #101828;
  background: #ffffff;
  outline: none;
  resize: vertical;
}

.account-context-chat__suggestion {
  min-height: 140px;
}

.account-context-chat__input {
  min-height: 64px;
}

.account-context-chat__suggestion:focus,
.account-context-chat__input:focus {
  border-color: #7f56d9;
}

.account-context-chat__suggestion--error {
  border-color: #f04438;
}

.account-context-chat__suggestion:disabled,
.account-context-chat__input:disabled {
  color: #98a2b3;
  background: #f9fafb;
  cursor: not-allowed;
}

.account-context-chat__counter-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.account-context-chat__reduce {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  color: #d92d20;
}

.account-context-chat__counter {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #667085;
}

.account-context-chat__counter--error {
  color: #d92d20;
}

.account-context-chat__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.account-context-chat__error {
  margin: 0;
  padding: 0 20px 16px;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17px;
  color: #d92d20;
}
</style>
